<template>
    <component
        :is="tag"
        v-bind="$attrs"
        class="inline-flex justify-center items-center border rounded shadow-sm | transition ease-in-out duration-150 | focus:outline-none focus:z-10"
        :class="[colorClassName, sizeClassName, fontSizeClassName, {
            'pointer-events-none select-none': loading,
            'cursor-not-allowed': disabled,
            'focus-visible:ring-2 focus-visible:ring-offset-2': !loading && !disabled
        }]"
        :disabled="disabled"
        :loading="loading"
        :tabindex="loading || disabled ? -1 : 0"
        :to="to"
        :type="type"
        ref="button"
        v-on="listeners"
    >
        <Spinner class="absolute h-4.5 w-4.5" :light="lightSpinner" v-if="loading" />
        <span class="inline-flex justify-center items-center" :class="{ 'opacity-0': loading }">
            <slot />
        </span>
    </component>
</template>

<script>
    import Spinner from '../Spinner/Spinner.vue';

    export const colors = ['white', 'blue', 'red'];
    export const tags = ['a', 'button', 'router-link'];
    export const buttonTypes = ['button', 'submit'];
    export const sizes = ['xs', 'sm', 'md', 'lg', 'xl'];

    /**
     * Basic component to display action in a page.
     * @displayName Button
     */
    export default {
        name: 'ActivixButton',

        components: {
            Spinner,
        },

        props: {
            color: {
                type: String,
                default: 'white',
                validator: (value) => colors.includes(value),
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            tag: {
                type: String,
                default: 'button',
                validator: (value) => tags.includes(value),
            },
            to: {
                type: null,
                default: null,
            },
            type: {
                type: String,
                default: 'button',
                validator: (value) => buttonTypes.includes(value),
            },
            size: {
                type: String,
                default: 'md',
                validator: (value) => sizes.includes(value),
            },
        },

        computed: {
            lightSpinner() {
                return !['white'].includes(this.color);
            },

            colorClassName() {
                switch (this.color) {
                    case 'blue':
                        return this.disabled
                            ? 'text-gray-550 bg-gray-200 border-gray-300 shadow-none'
                            : 'text-white bg-blue-500 border-blue-550 | hover:text-blue-100 hover:bg-blue-550 hover:border-blue-600 | active:text-blue-100 active:bg-blue-600 active:border-blue-700 | focus-visible:ring-blue-500';

                    case 'red':
                        return this.disabled
                            ? 'text-gray-550 bg-gray-200 border-gray-300 shadow-none'
                            : 'text-white bg-red-500 border-red-550 | hover:text-red-100 hover:bg-red-550 hover:border-red-600 | active:text-red-100 active:bg-red-600 active:border-red-700 | focus-visible:ring-red-500';

                    case 'white':
                    default:
                        return this.disabled
                            ? 'text-gray-550 bg-gray-200 border-gray-300 shadow-none'
                            : 'text-gray-700 bg-white border-gray-300 | hover:text-gray-800 hover:bg-gray-100 hover:border-gray-300 | active:bg-gray-150 active:border-gray-350 | focus-visible:ring-blue-500';
                }
            },

            sizeClassName() {
                return {
                    xs: 'px-2.5 py-1',
                    sm: 'px-3 py-1.5',
                    md: 'px-4 py-2',
                    lg: 'px-5 py-2.5',
                    xl: 'px-6 py-3',
                }[this.size];
            },

            fontSizeClassName() {
                if (this.$ui.oldFontScale) {
                    return {
                        xs: 'text-sm',
                        sm: 'text-sm',
                        md: 'text-base',
                        lg: 'text-lg',
                        xl: 'text-lg',
                    }[this.size];
                }

                return {
                    xs: 'text-xs',
                    sm: 'text-xs',
                    md: 'text-sm',
                    lg: 'text-base',
                    xl: 'text-base',
                }[this.size];
            },

            listeners() {
                return {
                    ...this.$listeners,
                    click: this.onClick,
                };
            },
        },

        methods: {
            onClick(e) {
                if (this.disabled) {
                    e.stopPropagation();
                }

                if (!this.disabled && !this.loading) {
                    this.$emit('click');
                }
            },

            focus() {
                this.$refs.button.focus();
            },
        },
    };
</script>
